import { useQuery } from '@apollo/client';
import useUploadDialogActions from 'FeatureModules/Document/hooks/useUploadDialogActions';
import { IMAGE_FILE_TYPES } from 'Components/FileUpload/Dropzone';
import { EFilterType, TFilter, TFilterEvent } from 'Components/Filter/filter.types';
import { DOCUMENTS_PER_PAGE } from 'utils/pagination.utils';
import { GET_DOCUMENTS } from 'graphql/secure/queries';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { EDocumentCategory, IBaseDocument } from 'FeatureModules/Document/types/document.types';
import { IGraphQlConnection } from 'types/core.types';
import {
  ArrayParam,
  createEnumArrayParam,
  ObjectParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { removeGraphConnections } from 'utils/graphql.utils';
import useDialog from 'hooks/useDialog';
import { useLogEvent } from 'hooks/useLogEvent';

const DATE_FORMAT = 'YYYY-MM-DD';

const useInternalDocuments = () => {
  const [query, setQuery] = useQueryParams(
    {
      searchTerm: withDefault(StringParam, ''),
      creationDateRange: ObjectParam,
      categories: withDefault(createEnumArrayParam(Object.keys(EDocumentCategory)), null),
      siteIds: withDefault(ArrayParam, undefined),
    },
    { removeDefaultsFromUrl: true }
  );

  const { searchTerm, creationDateRange, categories, siteIds } = query;

  const { data, loading, error, fetchMore } = useQuery<{
    documents: IGraphQlConnection<IBaseDocument>;
  }>(GET_DOCUMENTS, {
    variables: {
      filters: {
        searchTerm,
        creationDateRange,
        categories,
        siteIds,
        fileExtensions: ['pdf', ...IMAGE_FILE_TYPES],
      },
      first: DOCUMENTS_PER_PAGE,
    },
  });

  const { logEvent } = useLogEvent();
  const { openDialog } = useDialog();
  const { handleDeleteDocuments } = useUploadDialogActions();

  const [selectedForDeletion, setSelectedForDeletion] = useState<string[]>([]);
  const [usageSelectedDocuments, setUsageSelectedDocuments] = useState(0);

  const documents: IBaseDocument[] = useMemo(
    () => (data?.documents ? removeGraphConnections(data?.documents) : []),
    [data]
  );

  const hasNextPage: boolean = !!data?.documents?.pageInfo?.hasNextPage;
  const endCursor: string | undefined = data?.documents?.pageInfo?.endCursor;

  const filters: TFilter[] = useMemo(
    () => [
      {
        type: EFilterType.DOCUMENT_CATEGORY,
        value: categories,
      },
      {
        type: EFilterType.SITES,
        value: (siteIds?.filter(Boolean) || undefined) as string[] | undefined,
      },
      {
        type: EFilterType.TIME_FRAME,
        label: 'Upload date',
        value:
          creationDateRange?.startDate || creationDateRange?.endDate
            ? {
                startDate: creationDateRange?.startDate
                  ? new Date(creationDateRange.startDate)
                  : undefined,
                endDate: creationDateRange?.endDate
                  ? new Date(creationDateRange.endDate)
                  : undefined,
              }
            : null,
      },
    ],
    [categories, creationDateRange, siteIds]
  );

  const handlePageEndReached = () => {
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  useEffect(() => {
    if (!documents?.length && hasNextPage) {
      handlePageEndReached();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents, hasNextPage]);

  useEffect(() => {
    if (selectedForDeletion.length) {
      const usedDocuments = documents.filter(({ id }) => selectedForDeletion.includes(id));
      setUsageSelectedDocuments(
        usedDocuments.reduce((current, { usageCount }) => current + (usageCount || 0), 0)
      );
    }
  }, [documents, selectedForDeletion]);

  const handlePrepareDeletion = () => {
    openDialog({
      type: 'ALERT',
      props: {
        displayCloseButton: true,
        title: `Delete ${selectedForDeletion.length === 1 ? 'document' : 'documents'}`,
        submitText: 'Delete',
        itemTitle: selectedForDeletion.length === 1 ? '' : `${selectedForDeletion.length} selected`,
        text:
          selectedForDeletion.length === 1
            ? 'Are you sure you want to delete this document? You will not be able to restore it.'
            : 'Are you sure you want to delete these documents? You will not be able to restore them.',
        onSubmit: handleDelete,
      },
    });
  };

  const handleClickDelete: (document: IBaseDocument) => void = ({ id, title }) => {
    openDialog({
      type: 'ALERT',
      props: {
        displayCloseButton: true,
        title: 'Delete document',
        submitText: 'Delete',
        itemTitle: title,
        text: 'Are you sure you want to delete this document? You will not be able to restore it.',
        onCancel: () => undefined,
        onSubmit: () => handleDeleteSingleDocument(id),
      },
    });
  };

  const handleDeleteSingleDocument = async (documentId: string) => {
    await handleDeleteDocuments({
      variables: {
        ids: [documentId],
      },
    });
  };

  const handleDelete = async () => {
    await handleDeleteDocuments({
      variables: {
        ids: selectedForDeletion,
      },
    });
    if (hasNextPage && endCursor) {
      await fetchMore({
        variables: {
          first: selectedForDeletion.length,
          after: endCursor,
        },
      });
    }
    setSelectedForDeletion([]);
  };

  const handleFilterChange = (event: TFilterEvent) => {
    switch (event.type) {
      case EFilterType.TIME_FRAME: {
        const { startDate, endDate } = event.value || {};

        setQuery({
          creationDateRange: {
            startDate: startDate ? moment(startDate).format(DATE_FORMAT) : undefined,
            endDate: endDate ? moment(endDate).format(DATE_FORMAT) : undefined,
          },
        });
        break;
      }
      case EFilterType.DOCUMENT_CATEGORY: {
        setQuery({
          ...event,
          categories: event.value,
        });
        break;
      }
      case EFilterType.SITES: {
        setQuery({
          ...event,
          siteIds: event.value || undefined,
        });
        break;
      }
      default:
        break;
    }
    logEvent('INTERNAL_DOCUMENTS_FILTERED', { type: event.type });
  };

  const handleFilterReset = () => {
    setQuery({
      searchTerm: '',
      creationDateRange: undefined,
      categories: undefined,
      siteIds: undefined,
    });
  };

  const handleSearchTermChange = (searchTerm: string) => {
    setQuery({ searchTerm });
    logEvent('INTERNAL_DOCUMENTS_SEARCHED', { searchTerm });
  };

  return {
    error,
    loading,
    documents,
    selectedForDeletion,
    usageSelectedDocuments,
    searchTerm,
    endCursor,
    hasNextPage,
    filters,
    handleFilterChange,
    handleFilterReset,
    setSelectedForDeletion,
    handleSearchTermChange,
    handleClickDelete,
    handleDelete,
    handlePrepareDeletion,
    handlePageEndReached,
  };
};

export default useInternalDocuments;
